import { OpenAIRealtimeWebRTC } from "@openai/agents/realtime";

export function createStudyBuddyRealtimeTransport(mediaStream) {
  const transport = new OpenAIRealtimeWebRTC({ mediaStream });
  const eventHandlers = new Set(), functionHandlers = new Set(), disconnectedHandlers = new Set(), audioStartHandlers = new Set(), audioStoppedHandlers = new Set(), calls = new Map();
  let closed = false, assistantAudioActive = false, moveOnPending = false;
  const sdkListeners = [];
  const transcriptHandlers = new Set(), transcriptIds = new Set();
  const listen = (type, handler) => {
    transport.on(type, handler);
    sdkListeners.push([type, handler]);
  };

  listen("*", (event) => {
    if (closed) return;
    // Verified against agents-realtime 0.8.2's final audio transcript schemas.
    const speaker = event?.type === "conversation.item.input_audio_transcription.completed" ? "You" : event?.type === "response.output_audio_transcript.done" ? "Study Buddy" : null;
    const text = typeof event?.transcript === "string" ? event.transcript.trim() : "";
    if (speaker && text && !text.includes("APPLICATION_CONTROL_MOVE_ON_CURRENT_QUESTION")) {
      const stableId = event.item_id ? `${speaker}:${event.item_id}:${event.content_index ?? 0}` : event.event_id;
      if (!stableId || !transcriptIds.has(stableId)) {
        if (stableId) transcriptIds.add(stableId);
        transcriptHandlers.forEach((handler) => handler({ id: stableId || null, speaker, text }));
      }
    }
    // WebRTC playback lifecycle, unlike response.done, reflects the browser output buffer.
    if (event?.type === "output_audio_buffer.started" && !assistantAudioActive) {
      assistantAudioActive = true;
      audioStartHandlers.forEach((handler) => handler());
    } else if (event?.type === "output_audio_buffer.stopped" && assistantAudioActive) {
      assistantAudioActive = false;
      moveOnPending = false;
      audioStoppedHandlers.forEach((handler) => handler());
    }
    eventHandlers.forEach((handler) => handler(event));
  });
  listen("function_call", (call) => {
    if (closed) return;
    const callId = typeof call?.callId === "string" ? call.callId.trim() : "";
    if (!callId) { eventHandlers.forEach((handler) => handler({ type: "study_buddy.protocol_error" })); return; }
    calls.set(callId, call);
    functionHandlers.forEach((handler) => handler({ callId, name: call.name || "", arguments: call.arguments || "" }));
  });
  listen("error", () => { if (!closed && moveOnPending) disconnectedHandlers.forEach((handler) => handler()); });
  listen("connection_change", (status) => { if (!closed && status === "disconnected") disconnectedHandlers.forEach((handler) => handler()); });

  return {
    async connect({ clientSecret, model }) {
      // No instructions or tools are supplied, so the backend-created session configuration remains authoritative.
      try { return await transport.connect({ apiKey: clientSecret, model }); }
      finally { if (closed) transport.close(); }
    },
    onEvent(handler) { eventHandlers.add(handler); return () => eventHandlers.delete(handler); },
    onTranscript(handler) { transcriptHandlers.add(handler); return () => transcriptHandlers.delete(handler); },
    onFunctionCall(handler) { functionHandlers.add(handler); return () => functionHandlers.delete(handler); },
    onDisconnected(handler) { disconnectedHandlers.add(handler); return () => disconnectedHandlers.delete(handler); },
    onAssistantAudioStart(handler) { audioStartHandlers.add(handler); return () => audioStartHandlers.delete(handler); },
    onAssistantAudioStopped(handler) { audioStoppedHandlers.add(handler); return () => audioStoppedHandlers.delete(handler); },
    requestResponse() {
      if (closed) return false;
      // SDK 0.8.2 maps this directly to a bare response.create event.
      transport.requestResponse();
      return true;
    },
    requestMoveOnCurrentQuestion() {
      if (closed || transport.status !== "connected" || moveOnPending) return false;
      // Keep the latch on failure: delivery may have succeeded. Recovery uses a new transport.
      moveOnPending = true;
      transport.sendMessage("APPLICATION_CONTROL_MOVE_ON_CURRENT_QUESTION", {}, { triggerResponse: false });
      transport.requestResponse();
      return true;
    },
    sendFunctionCallOutput({ callId, output, continueResponse }) {
      const original = calls.get(callId);
      if (!original || closed) return false;
      transport.sendFunctionCallOutput(original, output, continueResponse === true);
      return true;
    },
    mute(value) { if (!closed) transport.mute(value); },
    close() { if (closed) return; closed = true; assistantAudioActive = false; transcriptHandlers.clear(); transcriptIds.clear(); eventHandlers.clear(); functionHandlers.clear(); disconnectedHandlers.clear(); audioStartHandlers.clear(); audioStoppedHandlers.clear(); calls.clear(); sdkListeners.forEach(([type, handler]) => transport.off?.(type, handler)); sdkListeners.length = 0; transport.close(); },
  };
}
